import { useRef } from 'react';

import { IMAGE_EXTENSIONS, type Media, type MediaEvent, MediaType, type MediaView } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import cn from '@cobliteam/ui/utils/cn';

export interface MediaDialogPlayerProps {
  mediaView: MediaView;
}

const isImage = (url: string) => IMAGE_EXTENSIONS.map((x) => url.includes(x)).reduce((extension, hasIt) => extension || hasIt);

const MediaDialogPlayer = ({ mediaView }: MediaDialogPlayerProps) => {
  const mediaUrl = mediaView.mediaUrl;
  const mediaInfo = mediaView.mediaInfo;
  const videos = mediaView.videos;
  const hasMultipleVideos = videos.length > 1;

  const playerRefs = videos.map((_) => useRef<HTMLVideoElement | null>(null), []);

  const handleVideoPlay = (mediaData: Media, path?: string) => {
    playerRefs.forEach((playerRef) => playerRef.current?.play());
    const mediaType = mediaData?.mediaType;
    const isFavorite = mediaData?.isFavorite;
    const eventType = mediaType === MediaType.EVENT_VIDEO ? (mediaData.mediaDetails as MediaEvent).eventTrigger : mediaType;
    const eventSeverity = mediaData.severity;

    Analytics.track('Dashcam Video Played', eventSeverity ? { path, eventType, eventSeverity, isFavorite } : { path, eventType, isFavorite });
  };

  const handleVideoEnded = (mediaData: Media, path?: string) => {
    const mediaType = mediaData?.mediaType;
    const eventType = mediaType === MediaType.EVENT_VIDEO ? (mediaData.mediaDetails as MediaEvent).eventTrigger : mediaType;
    const eventSeverity = mediaData.severity;

    Analytics.track('Dashcam Video Watched Fully', eventSeverity ? { path, eventType, eventSeverity } : { path, eventType });
  };

  const handleMediaPlay = () => handleVideoPlay(mediaInfo, origin);

  const handleMediaEnded = () => handleVideoEnded(mediaInfo, origin);

  const handlePause = () => {
    playerRefs.forEach((playerRef) => playerRef.current?.pause());
  };

  const renderMediaPlayer = () => {
    if (isImage(mediaUrl)) {
      return (
        <a href={mediaUrl} target='_blank' rel='noopener noreferrer'>
          <img src={mediaUrl} className='w-full h-auto rounded min-h-72' alt='' />
        </a>
      );
    }

    return (
      <>
        <div className={cn('flex border-box overflow-hidden bg-gray-light rounded', hasMultipleVideos ? 'grid grid-cols-2' : '')}>
          {videos.map((video, index) => (
            <div key={index}>
              <video
                ref={playerRefs[index]}
                key={video.url}
                className={cn(hasMultipleVideos ? 'grid grid-cols-subgrid w-[430px] h-auto' : 'w-[860px] h-auto')}
                onPlay={handleMediaPlay}
                onPause={handlePause}
                onEnded={handleMediaEnded}
                controls
                muted
              >
                <source src={video.url} type='video/mp4' />
              </video>
            </div>
          ))}
        </div>
      </>
    );
  };

  return <div className='flex items-center justify-center py-3 px-5'>{renderMediaPlayer()}</div>;
};

export default MediaDialogPlayer;
